/* eslint-disable react-refresh/only-export-components */
import type { PresentationData } from '../../types/presentation'
import {
  EnhancedContentSlide,
  EnhancedGridSlide,
  FeatureCard,
} from '../../components/presentation'
import { Card } from '../../components/ui/card'

/**
 * AI Consulting Initial Meeting Presentation
 *
 * Initial consultation presentation for AI consulting and RAG systems.
 * Updated with homepage colors and professional styling.
 */

// ============================================
// CUSTOM COMPONENTS FOR THIS PRESENTATION
// ============================================

interface GeometricTitleSlideProps {
  title: string
  subtitle?: string
  author1?: { name: string; role: string }
  author2?: { name: string; role: string }
  valueProp1?: { text: string }
  valueProp2?: { text: string }
  valueProp3?: { text: string }
  sessionDuration?: string
  sessionConfidentiality?: string
  sessionAgenda?: string
}

function GeometricTitleSlide({
  subtitle,
  author1,
  author2,
  valueProp1,
  valueProp2,
  valueProp3,
  sessionDuration,
  sessionConfidentiality,
  sessionAgenda,
}: GeometricTitleSlideProps) {
  return (
  <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-600 dark:from-orange-600 dark:via-red-600 dark:to-yellow-500">
    {/* Animated Background Pattern */}
    <div className="absolute inset-0 overflow-hidden opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>
    </div>

    {/* Gradient Overlay */}
    <div className="absolute inset-0 bg-gradient-radial from-white/10 via-transparent to-transparent" />

    {/* Content Grid */}
    <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-16 h-full px-8 lg:px-20 py-16 pb-24 items-center">
      {/* Left Column */}
      <div className="space-y-6 lg:space-y-8">
        {/* Company Badge */}
        <div className="inline-flex items-center gap-3 bg-white/20 border border-white/30 px-6 py-3 rounded-full text-white font-medium backdrop-blur-sm">
          <span>AI Expertise</span>
        </div>

        {/* Main Title */}
        <h1 className="text-5xl lg:text-7xl font-light text-white leading-tight">
          <span className="font-bold">AI</span>
          <span className="text-white/70 font-extralight"> </span>
          <span className="font-normal">Consulting</span>
          <br />
          <span className="font-extralight italic text-white/80 text-4xl lg:text-6xl">&</span>{' '}
          <span className="font-normal">Development</span>
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <h2 className="text-2xl lg:text-3xl font-light text-white/90 leading-relaxed">
            <span className="font-medium">Initial Meeting:</span>
            <br />
            {subtitle}
          </h2>
        )}

        {/* Value Props */}
        <div className="flex flex-wrap gap-4 lg:gap-6">
          {valueProp1 && (
            <div className="flex items-center gap-3 bg-white/15 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/25">
              <span>{valueProp1.text}</span>
            </div>
          )}
          {valueProp2 && (
            <div className="flex items-center gap-3 bg-white/15 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/25">
              <span>{valueProp2.text}</span>
            </div>
          )}
          {valueProp3 && (
            <div className="flex items-center gap-3 bg-white/15 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/25">
              <span>{valueProp3.text}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-6 lg:space-y-8">
        {/* Presenter Card */}
        {(author1 || author2) && (
          <Card className="bg-white/15 backdrop-blur-md border-white/25 text-white">
            <div className="p-6 lg:p-8">
              <h3 className="text-xl lg:text-2xl font-semibold text-white mb-6">Your Experts</h3>
              <div className="space-y-6">
                {author1 && (
                  <div className="flex items-center gap-4">
                    <img
                      src="/images/jan.png"
                      alt={author1.name}
                      className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold">{author1.name}</h4>
                      <p className="text-white/90 text-sm font-medium">{author1.role}</p>
                    </div>
                  </div>
                )}
                {author2 && (
                  <div className="flex items-center gap-4">
                    <img
                      src="/images/kai.png"
                      alt={author2.name}
                      className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold">{author2.name}</h4>
                      <p className="text-white/90 text-sm font-medium">{author2.role}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Session Details */}
        {(sessionDuration || sessionConfidentiality || sessionAgenda) && (
          <Card className="bg-black/20 backdrop-blur-md border-white/15 text-white">
            <div className="p-6 lg:p-8 space-y-4">
              {sessionDuration && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Duration</div>
                    <div className="font-medium text-white">{sessionDuration}</div>
                  </div>
                </div>
              )}
              {sessionConfidentiality && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Confidentiality</div>
                    <div className="font-medium text-white">{sessionConfidentiality}</div>
                  </div>
                </div>
              )}
              {sessionAgenda && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Agenda</div>
                    <div className="font-medium text-white">{sessionAgenda}</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>

    <style>{`
      .bg-gradient-radial {
        background: radial-gradient(ellipse at top right, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
      }
    `}</style>
  </div>
  )
}

// Numbered Benefit Card with clean styling
interface NumberedBenefitCardProps {
  number: string
  title: string
  description: string
}

function NumberedBenefitCard({ number, title, description }: NumberedBenefitCardProps) {
  return (
  <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:-translate-y-1 transition-transform duration-300 shadow-lg">
    <div className="p-6">
      <div className="text-5xl font-bold text-blue-600 dark:text-orange-500 mb-4">{number}</div>
      <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{title}</h4>
      <p className="text-gray-600 dark:text-gray-300 font-medium">{description}</p>
    </div>
  </Card>
  )
}

// Architecture Layer Component
interface ArchLayerProps {
  title: string
  description: string
  gradient: string
}

function ArchLayer({ title, description, gradient }: ArchLayerProps) {
  return (
    <div className={`p-4 lg:p-5 rounded-2xl text-white shadow-lg hover:translate-x-2 transition-transform ${gradient}`}>
      <h4 className="text-lg lg:text-xl font-semibold mb-1">
        {title}
      </h4>
      <p className="text-white/90 text-sm lg:text-base">{description}</p>
    </div>
  )
}

// ============================================
// PRESENTATION DATA
// ============================================

export const erstgespraechEn: PresentationData = {
  metadata: {
    title: 'AI Consulting Initial Meeting',
    subtitle: 'Structured Initial Meeting for AI Consulting and RAG Systems',
    author: 'Jan Pfrommer & Kai Bennett',
    date: '2025',
    duration: 90,
    tags: ['AI', 'Consulting', 'RAG', 'Business'],
    description: 'Initial consultation presentation for AI advisory services, covering client needs assessment, AI maturity, and solution options.',
  },
  slides: [
    // Slide 1: Custom Title Slide
    {
      id: 1,
      component: () => (
        <GeometricTitleSlide
          title="AI Consulting & Development"
          subtitle="Your Path to Successful AI Integration"
          author1={{ name: 'Jan Pfrommer', role: 'AI Strategy & Implementation' }}
          author2={{ name: 'Kai Bennett', role: 'Software Architect' }}
          valueProp1={{ text: 'Strategic Consulting' }}
          valueProp2={{ text: 'RAG Systems' }}
          valueProp3={{ text: 'Practical Training' }}
          sessionDuration="60-90 Minutes"
          sessionConfidentiality="Fully Guaranteed"
          sessionAgenda="Structured & Efficient"
        />
      ),
      transition: 'fade',
    },

    // Slide 2: Meeting Objectives
    {
      id: 2,
      component: () => (
        <EnhancedContentSlide
          title="Meeting Objectives"
          subtitle="What we want to achieve together today"
          variant="light"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <NumberedBenefitCard
              number="01"
              title="Understand Your Situation"
              description="Learn about your current challenges and pain points"
            />
            <NumberedBenefitCard
              number="02"
              title="Assess AI Maturity"
              description="Evaluate technical and organizational prerequisites"
            />
            <NumberedBenefitCard
              number="03"
              title="Find Suitable Solutions"
              description="Consulting, training, or development - what makes sense for you?"
            />
            <NumberedBenefitCard
              number="04"
              title="Demonstrate Feasibility"
              description="Live demo of our RAG system - not theory, but working technology"
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 3: Agenda
    {
      id: 3,
      component: () => (
        <EnhancedContentSlide
          title="Meeting Flow"
          subtitle="60-90 minutes structured and efficient"
          variant="light"
        >
          <div className="flex flex-col gap-2 mt-4 max-w-4xl mx-auto pb-24">
            <ArchLayer
              title="1. Introduction (5 min)"
              description="Introductions • Clarify goals • Framework"
              gradient="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="2. Client Context (15-20 min)"
              description="Processes • AI experience • Data sources • Requirements"
              gradient="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-red-600 dark:to-yellow-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="3. Demo & Insights (15-25 min)"
              description="RAG system live • Best practices • Highlight potential"
              gradient="bg-gradient-to-r from-purple-600 to-pink-600 dark:from-yellow-600 dark:to-orange-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="4. Service Modules (10-15 min)"
              description="Modular offerings • Find suitable solutions"
              gradient="bg-gradient-to-r from-pink-600 to-rose-600 dark:from-orange-600 dark:to-red-600"
            />
            <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>
            <ArchLayer
              title="5. Next Steps (5-10 min)"
              description="Summary • Pilot project • Concrete steps"
              gradient="bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-green-600 dark:to-emerald-600"
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 4: Kai Bennett (Presenter Slide)
    {
      id: 4,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-700 to-slate-900 dark:from-gray-800 dark:to-gray-950 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-6 pb-24">
            <div className="max-w-6xl text-white w-full">
              {/* Header with Image and Name */}
              <div className="flex items-center gap-6 mb-6">
                <img
                  src="/images/kai.png"
                  alt="Kai Bennett"
                  className="w-28 h-28 rounded-2xl object-cover shadow-2xl flex-shrink-0"
                />
                <div className="text-left">
                  <h2 className="text-4xl font-bold">Kai Bennett</h2>
                  <p className="text-xl font-medium text-white/90 mt-1">
                    <strong>Software Architect</strong>
                  </p>
                  <p className="text-base text-white/80 mt-1">
                    10+ years experience in .NET development • AI & RAG systems since 2023 • Certified Software Architect (iSAQB CPSA-F CPSA-A)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column: Education & Certifications */}
                <div className="space-y-4">
                  {/* Education */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-2 text-sm font-semibold">
                      Education
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="text-white/90">
                        <strong>B.Sc. Applied Computer Science</strong><br />
                        <span className="text-white/70">FHDW | 2013–2015</span>
                      </div>
                      <div className="text-white/90">
                        <strong>IT Technical Assistant</strong><br />
                        <span className="text-white/70">Lippe Berufskolleg | 2010–2013</span>
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-3 text-sm font-semibold">
                      Certifications
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <img
                          src="https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png"
                          alt="iSAQB CPSA-F"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                        />
                        <p className="text-xs text-white/90">
                          <strong>CPSA-F</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png"
                          alt="iSAQB CPSA-A"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                        />
                        <p className="text-xs text-white/90">
                          <strong>CPSA-A</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://eu.images.credential.net/badge/tiny/ku4bl7ci_1771016124466_badge.png"
                          alt="iSAQB CPSA-F CPSA-A"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          CPSA
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>CPSA-F/A</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        AI & Infrastructure
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Open-Source LLMs • Document Intelligence</li>
                        <li>• AI Search • Container Apps</li>
                        <li>• Functions & Service Bus • SQL Databases</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Development
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Python (FastAPI, RAG) • C# & .NET</li>
                        <li>• Qdrant Vector DB • Docker & Kubernetes</li>
                        <li>• REST APIs • Microservices</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Architecture & DevOps
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Software Architecture • Infrastructure Setup</li>
                        <li>• DevOps • CI/CD Pipelines</li>
                        <li>• Dapr & Service Mesh • IaC</li>
                      </ul>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 5: Jan Pfrommer (Presenter Slide)
    {
      id: 5,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-indigo-700 to-purple-900 dark:from-gray-800 dark:to-gray-950 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-6 pb-24">
            <div className="max-w-6xl text-white w-full">
              {/* Header with Image and Name */}
              <div className="flex items-center gap-6 mb-6">
                <img
                  src="/images/jan.png"
                  alt="Jan Pfrommer"
                  className="w-28 h-28 rounded-2xl object-cover shadow-2xl flex-shrink-0"
                />
                <div className="text-left">
                  <h2 className="text-4xl font-bold">Jan Pfrommer</h2>
                  <p className="text-xl font-medium text-white/90 mt-1">
                    <strong>Software Engineer & AI Engineer</strong>
                  </p>
                  <p className="text-base text-white/80 mt-1">
                    Frontend, Backend & Requirements Engineering • Scrum Master & AI Projects
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column: Education & Certifications */}
                <div className="space-y-4">
                  {/* Education */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-2 text-sm font-semibold">
                      Education
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="text-white/90">
                        <strong>M.Sc. Information Systems</strong><br />
                        <span className="text-white/70">HS Pforzheim | 2017–2019</span>
                      </div>
                      <div className="text-white/90">
                        <strong>B.Sc. Business IT</strong><br />
                        <span className="text-white/70">HS Pforzheim | 2012–2017</span>
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="p-4 bg-white/10 rounded-xl border border-white/20">
                    <h4 className="text-white mb-3 text-sm font-semibold">
                      Certifications
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <img
                          src="https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg"
                          alt="CPRE-FL"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          CPRE
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>CPRE-FL</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://logodix.com/logo/649849.jpg"
                          alt="ISTQB CTFL"
                          className="w-16 h-16 mb-1 mx-auto object-contain bg-white/10 rounded-lg"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          ISTQB
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>ISTQB</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img
                          src="https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png"
                          alt="SAP BTP Developer"
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1 text-white text-xs font-bold" style={{ display: 'none' }}>
                          SAP
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>SAP BTP</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        AI & Context Engineering
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Context Engineering • AI Engineering</li>
                        <li>• Prompt Engineering • RAG Architecture</li>
                        <li>• GitHub Copilot • Model Context Protocol</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Development
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• TypeScript/JavaScript • Python • Java</li>
                        <li>• React • Vue.js • Flutter • Angular</li>
                        <li>• Spring Boot • FastAPI • SAP BTP</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Requirements & Agile
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Requirements Engineering • Testing</li>
                        <li>• Scrum Master • UX/UI Design</li>
                        <li>• Process Optimization • Consulting</li>
                      </ul>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 6: Our Approach
    {
      id: 6,
      component: () => (
        <EnhancedGridSlide
          title="Our Approach"
          subtitle="How we lead you to success"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="Enterprise-Ready"
              description="Scalable on-premise architectures. Production-ready from the start."
              variant="default"
            />,
            <FeatureCard
              title="AI Practice Since 2023"
              description="Daily work with Open-Source LLMs, RAG systems & AI integration."
              variant="default"
            />,
            <FeatureCard
              title="Privacy-First"
              description="On-premise or cloud - GDPR compliant and data secure."
              variant="default"
            />,
            <FeatureCard
              title="Agile & Holistic"
              description="From requirements to development to deployment. Scrum, DevOps, CI/CD."
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 7: Client Context - Questions
    {
      id: 7,
      component: () => (
        <EnhancedGridSlide
          title="Understanding Your Situation"
          subtitle="Important questions for precise assessment"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="Processes & Tasks"
              description="Which tasks or processes would you like to optimize or automate?"
              items={['Identify use cases', 'Assess potential']}
              variant="default"
            />,
            <FeatureCard
              title="AI Experience"
              description="Do you already have touchpoints with AI? (Copilot, ChatGPT, Open-Source LLMs)"
              items={['Determine maturity', 'Leverage experience']}
              variant="default"
            />,
            <FeatureCard
              title="Data Sources"
              description="What data is available? (Documents, databases, knowledge systems)"
              items={['Check quality', 'Secure access']}
              variant="default"
            />,
            <FeatureCard
              title="Compliance & Privacy"
              description="What requirements exist? Is on-prem or cloud solution possible?"
              items={['GDPR compliance', 'Security standards']}
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 8: AI Maturity Level
    {
      id: 8,
      component: () => (
        <EnhancedContentSlide
          title="Determine AI Maturity"
          subtitle="Where are you currently?"
          variant="light"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-8">
            <Card className="flex-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🌱</div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Curious</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">First Steps</p>
              <small className="text-gray-500 dark:text-gray-400">"What can AI do?"</small>
            </Card>

            <div className="text-4xl text-blue-600 dark:text-orange-500 font-bold">→</div>

            <Card className="flex-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🔬</div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Experimental</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">Trying Out</p>
              <small className="text-gray-500 dark:text-gray-400">ChatGPT, Copilot</small>
            </Card>

            <div className="text-4xl text-blue-600 dark:text-orange-500 font-bold">→</div>

            <Card className="flex-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center p-6 hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🚀</div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Pilot Phase</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">First Projects</p>
              <small className="text-gray-500 dark:text-gray-400">Internal Testing</small>
            </Card>

            <div className="text-4xl text-blue-600 dark:text-orange-500 font-bold">→</div>

            <Card className="flex-1 bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 text-center p-6 shadow-lg">
              <div className="text-4xl mb-4">✅</div>
              <h4 className="text-xl font-semibold mb-2 text-white">Production</h4>
              <p className="text-white/90 mb-2 font-medium">Live Operations</p>
              <small className="text-white font-semibold">Production-Ready</small>
            </Card>
          </div>

          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 mt-8">
            <div className="p-6 text-center">
              <p className="text-lg text-white font-medium">
                <strong>Important:</strong> Each phase has its own requirements.
                We meet you exactly where you are.
              </p>
            </div>
          </Card>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 9: Demo Announcement
    {
      id: 9,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-600 dark:from-orange-600 dark:via-red-600 dark:to-yellow-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-8 text-center">
            <div className="max-w-4xl space-y-12 text-white">
              <div className="text-6xl lg:text-7xl font-bold drop-shadow-lg">
                🚀
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold drop-shadow-lg">
                Live Demo
              </h1>
              <h2 className="text-3xl lg:text-4xl font-light drop-shadow-md">
                Our Enterprise RAG System in Action
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                See how your team can retrieve internal knowledge data<br />
                faster and more precisely
              </p>
              <Card className="bg-white/20 border-white/30 backdrop-blur-md text-white mx-auto max-w-2xl">
                <div className="p-8 text-center">
                  <div className="text-4xl mb-6">✅</div>
                  <h4 className="text-2xl font-semibold mb-4">Standard LLM vs. RAG-LLM</h4>
                  <p className="text-white/90 text-lg leading-relaxed">
                    See the difference: General answers vs. contextualized,
                    fact-based answers with source citations
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 10: Service Modules
    {
      id: 10,
      component: () => (
        <EnhancedGridSlide
          title="Modular Service Offerings"
          subtitle="Flexible solutions for your requirements"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="AI Strategy & Training"
              description="Enable employees, find your own use cases"
              items={[
                'Developer workshops',
                'Prompt engineering training',
                'Best practice sessions',
              ]}
              variant="default"
            />,
            <FeatureCard
              title="RAG System & Knowledge Platform"
              description="Make your own data usable securely with LLMs"
              items={[
                'On-prem or cloud (Open-Source LLMs)',
                'Qdrant vector database',
                'FastAPI backend',
              ]}
              variant="default"
            />,
            <FeatureCard
              title="AI Integration into Systems"
              description="Efficiency gains through automation"
              items={[
                'Chatbot integration',
                'Agent workflows',
                'API development',
              ]}
              variant="default"
            />,
            <FeatureCard
              title="Development & Support"
              description="Tailored solutions"
              items={[
                'Rapid prototyping',
                'Custom development',
                'Deployment & operations',
              ]}
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 11: Total Cost of Ownership (NEW SLIDE)
    {
      id: 11,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="relative z-10 p-6 lg:p-12 h-full flex flex-col justify-center pb-24">
            <div className="max-w-6xl mx-auto w-full">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                Total Cost of Ownership
              </h2>
              <h3 className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 mb-12 text-center">
                Cloud API vs. Local Inference (12 months)
              </h3>

              {/* TCO Table */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 text-white">
                      <th className="px-6 py-4 text-left font-semibold">Scenario</th>
                      <th className="px-6 py-4 text-center font-semibold">Cloud API (GPT-4o tier)</th>
                      <th className="px-6 py-4 text-center font-semibold">Local (RTX 5090 build)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">500K tokens/day</td>
                      <td className="px-6 py-5 text-center text-gray-600 dark:text-gray-300">~$6,000 – $9,000</td>
                      <td className="px-6 py-5 text-center font-semibold text-green-600 dark:text-green-400">~$2,500</td>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">1M tokens/day</td>
                      <td className="px-6 py-5 text-center text-gray-600 dark:text-gray-300">~$12,000 – $18,000</td>
                      <td className="px-6 py-5 text-center font-semibold text-green-600 dark:text-green-400">~$2,500</td>
                    </tr>
                    <tr className="bg-gray-50 dark:bg-gray-800/50">
                      <td className="px-6 py-5 font-medium text-gray-900 dark:text-white">5M tokens/day</td>
                      <td className="px-6 py-5 text-center text-gray-600 dark:text-gray-300">~$60,000 – $90,000</td>
                      <td className="px-6 py-5 text-center font-semibold text-green-600 dark:text-green-400">~$5,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Key Insight Card */}
              <Card className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 text-white border-0">
                <div className="p-6 text-center">
                  <p className="text-lg leading-relaxed">
                    <strong>Break-even point:</strong> A single RTX 5090 build typically pays for itself between
                    <span className="text-yellow-200 dark:text-yellow-300 font-bold"> 1-3 months </span>
                    of moderate API usage. After that, local inference runs at the cost of electricity.
                    <span className="block mt-2 text-white/80 italic">For high-volume use cases, the economics are not even close.</span>
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 12: Your Desk Is the New Data Center (NEW SLIDE)
    {
      id: 12,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, black 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 p-6 lg:p-12 h-full flex flex-col justify-center pb-24">
            <div className="max-w-5xl mx-auto w-full">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                Your Desk Is the New Data Center
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-12 text-center leading-relaxed">
                The shift to local LLMs is a structural change in how developers and organizations interact with AI
              </p>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Then Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="text-2xl font-bold text-gray-400 dark:text-gray-500 mb-4">2024</h4>
                  <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Three-day project requiring deep systems expertise</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Complex setup and configuration required</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Limited model options</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">✗</span>
                      <span>Steep learning curve</span>
                    </li>
                  </ul>
                </div>

                {/* Now Card */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-orange-600 dark:to-red-600 rounded-2xl p-8 shadow-lg text-white">
                  <h4 className="text-2xl font-bold mb-4">2025</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span><strong>10-minute setup</strong> with modern tools</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span>Models are <strong>good enough</strong> for production</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span>Hardware is <strong>affordable enough</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-300 mt-1">✓</span>
                      <span>Tools are <strong>simple enough</strong></span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Call to Action */}
              <Card className="bg-white dark:bg-gray-800 border-2 border-blue-600 dark:border-orange-500">
                <div className="p-8 text-center">
                  <p className="text-xl text-gray-900 dark:text-white leading-relaxed mb-4">
                    <strong className="text-blue-600 dark:text-orange-500">Start today:</strong> Pull a model with Ollama, run it, hit the local API from your application code.
                  </p>
                  <p className="text-lg text-gray-600 dark:text-gray-400 italic">
                    Once you experience the speed, the privacy, and the freedom from per-token billing,
                    the question stops being <span className="text-gray-400 dark:text-gray-500">"Should I run models locally?"</span> and becomes
                    <span className="text-blue-600 dark:text-orange-500 font-semibold"> "Why would I send this data anywhere else?"</span>
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 13: Best Practices Insights
    {
      id: 13,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="relative z-10 p-8 lg:p-16 h-full flex flex-col justify-center pb-24">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 text-center">Quick Wins</h2>
            <h3 className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 mb-12 text-center">Immediately Actionable Best Practices</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="p-8">
                  <div className="text-4xl mb-4 text-center">🧠</div>
                  <h4 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white text-center">Context Engineering</h4>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-center font-medium">
                    The new prompt engineering - up to 70% better results through structured contexts
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg text-sm space-y-4">
                    <div>
                      <strong className="text-gray-900 dark:text-white text-base">Context &gt; Prompts:</strong>
                      <div className="mt-2 space-y-2">
                        <div className="text-gray-600 dark:text-gray-400"><span className="text-red-500 font-semibold">✗ "Write a text"</span></div>
                        <div className="pl-4 border-l-4 border-green-500 space-y-1">
                          <div className="text-gray-700 dark:text-gray-300">✅ <strong className="text-green-600 dark:text-green-400">Context:</strong> "Business context, target audience CTOs, focus on business value"</div>
                          <div className="text-gray-700 dark:text-gray-300">✅ <strong className="text-green-600 dark:text-green-400">Role:</strong> "Technical editor with 10 years experience"</div>
                          <div className="text-gray-700 dark:text-gray-300">✅ <strong className="text-green-600 dark:text-green-400">Task:</strong> "200-word text about RAG benefits"</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="p-8">
                  <div className="text-4xl mb-4 text-center">📊</div>
                  <h4 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white text-center">RAG vs. Standard LLM</h4>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-center font-medium">
                    Why RAG for enterprise data?
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg text-sm space-y-4">
                    <div>
                      <strong className="text-gray-900 dark:text-white text-base">Standard LLM:</strong>
                      <ul className="mt-2 space-y-1 text-red-500 font-medium">
                        <li>• Outdated knowledge</li>
                        <li>• Hallucinations</li>
                        <li>• No sources</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-green-600 dark:text-green-400 text-base">RAG System:</strong>
                      <ul className="mt-2 space-y-1 text-green-600 dark:text-green-400 font-medium">
                        <li>• Current data</li>
                        <li>• Fact-based</li>
                        <li>• Source citations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 14: Typical Use Cases
    {
      id: 14,
      component: () => (
        <EnhancedGridSlide
          title="Typical Use Cases"
          subtitle="Where AI brings real value"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              title="Knowledge Management"
              description="Make internal documentation searchable. Accelerate onboarding."
              variant="default"
            />,
            <FeatureCard
              title="Customer Support"
              description="24/7 chatbot with access to support database and FAQs."
              variant="default"
            />,
            <FeatureCard
              title="Contract Analysis"
              description="Automatic extraction of relevant clauses from contracts."
              variant="default"
            />,
            <FeatureCard
              title="Code Assistance"
              description="Developer support with internal codebase and best practices."
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 15: Next Steps
    {
      id: 15,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
          <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center pb-28">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 text-center">Next Steps</h2>
            <h3 className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-8 text-center">What comes next?</h3>

            <div className="flex flex-col gap-2 max-w-4xl mx-auto">
              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">1</span>
                    Today
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    We summarize: Which areas have the greatest potential?
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">2</span>
                    Option: Workshop
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Analysis session (2-4h) • Identify use cases • Develop roadmap
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">3</span>
                    Option: Pilot Project
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    RAG prototype with your data • Prove feasibility • Quick win
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">4</span>
                    Option: Training
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Developer workshop (1-3 days) • Hands-on training • Start your own projects
                  </p>
                </div>
              </Card>

              <div className="text-center text-2xl text-blue-600 dark:text-orange-500 font-bold">↓</div>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                    <span className="w-8 h-8 bg-blue-600 dark:bg-orange-500 text-white rounded-full flex items-center justify-center text-sm">5</span>
                    Option: Video Course
                  </h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Flexible remote learning • Self-paced • Practical content & examples
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 16: Contact
    {
      id: 16,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-600 dark:from-orange-600 dark:via-red-600 dark:to-yellow-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-8 text-center pb-24">
            <div className="max-w-5xl space-y-8 text-white">
              <h2 className="text-5xl lg:text-6xl font-bold">Thank You!</h2>
              <h3 className="text-3xl lg:text-4xl font-light">Your Questions & Our Contact</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                <Card className="bg-white/15 backdrop-blur-md border-white/25 text-white">
                  <div className="p-8 text-center space-y-4">
                    <img
                      src="/images/jan.png"
                      alt="Jan Pfrommer"
                      className="w-24 h-24 rounded-2xl object-cover mx-auto"
                    />
                    <h4 className="text-2xl font-semibold">Jan Pfrommer</h4>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      jan.pfrommer@securesight.ai
                    </p>
                  </div>
                </Card>

                <Card className="bg-white/15 backdrop-blur-md border-white/25 text-white">
                  <div className="p-8 text-center space-y-4">
                    <img
                      src="/images/kai.png"
                      alt="Kai Bennett"
                      className="w-24 h-24 rounded-2xl object-cover mx-auto"
                    />
                    <h4 className="text-2xl font-semibold">Kai Bennett</h4>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      kai.bennett@securesight.ai
                    </p>
                  </div>
                </Card>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center mt-8">
                <button className="px-8 py-4 bg-white text-blue-600 dark:text-orange-600 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors shadow-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule Follow-up
                </button>
                <button className="px-8 py-4 bg-transparent text-white border-2 border-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-white/10 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Presentation as PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },
  ],
}

export default erstgespraechEn
